import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { useTranslate } from "@tolgee/react";

import SpinnerComponent from "../Components/GenericComponents/SpinnerComponent";
import PrimaryButton from "../Components/GenericComponents/PrimaryButton";
import { useNotify } from "../Helpers/Notifications";
import LocalStorage from "../Helpers/LocalStorage";
import {
  joinConferenceCallApiCall,
  joinConferenceWithTokenApiCall,
} from "../API/conferences/conferencesService";
import { useAuthService } from "../Context/AuthContext";
import useInAppBrowserEscape from "../hooks/useInAppBrowserEscape";
import InAppBrowserBanner from "../Components/GenericComponents/InAppBrowserBanner";

const ConferenceInPersonJoinPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslate();
  const { notifyError, notifySuccess } = useNotify();
  const { handleLoginUser, handleLogoutUser, updateUser, user, anonUser } = useAuthService();

  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [requiresSignIn, setRequiresSignIn] = useState(false);
  const [conference, setConference] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const joinRequestRef = useRef(false);
  const { showBanner: showInAppBrowserBanner } = useInAppBrowserEscape();

  const token = searchParams.get("token");
  const linkToken = searchParams.get("link_token");

  const startTranslationSession = useCallback(
    (conferenceData) => {
      if (!conferenceData?.id) {
        setIsLoading(false);
        setErrorMessage(t("general.something_wrong"));
        return;
      }

      setIsLoading(true);
      joinConferenceCallApiCall(
        conferenceData.id,
        (response) => {
          const callDetails =
            response?.data?.details ||
            response?.data?.call ||
            response?.data?.conference_call ||
            conferenceData.call;
          if (!callDetails?.uuid) {
            setIsLoading(false);
            setErrorMessage(t("general.something_wrong"));
            return;
          }

          setIsLoading(false);
          navigate("/conferences/in-person-translation", {
            replace: true,
            state: {
              conferenceId: conferenceData.id,
              callId: callDetails.id,
              callUuid: callDetails.uuid,
              conferenceTitle: conferenceData.title,
              allowChat: conferenceData.settings?.allow_chat !== false,
              allowPolls: conferenceData.settings?.allow_polls !== false,
            },
          });
        },
        () => {
          setIsLoading(false);
          setErrorMessage(t("general.something_wrong"));
        }
      );
    },
    [navigate, t]
  );

  const shouldShowOnboarding = (nextUser) => {
    if (!nextUser) return true;
    return !nextUser.name || nextUser.name.trim().length < 3;
  };

  useEffect(() => {
    if (joinRequestRef.current) {
      return;
    }
    joinRequestRef.current = true;

    if (!token && !linkToken) {
      joinRequestRef.current = false;
      setIsLoading(false);
      setErrorMessage(t("general.something_wrong"));
      return;
    }

    joinConferenceWithTokenApiCall(
      token,
      linkToken,
      (response) => {
        const data = response?.data || {};
        const nextUser = data.user;
        const nextConference = data.conference;
        const accessToken = data.token;
        const refreshToken = data.refresh_token;

        if (accessToken) {
          LocalStorage.setAuthenthicationToken(accessToken);
          LocalStorage.setSSOFlag(true);
        }
        if (refreshToken) {
          LocalStorage.setRefreshToken(refreshToken);
        }
        if (nextUser) {
          handleLoginUser(nextUser);
        }

        notifySuccess(t("login.successfull_log_in"));
        setConference(nextConference || null);
        if (shouldShowOnboarding(nextUser)) {
          setIsLoading(false);
          setShowOnboarding(true);
        } else {
          startTranslationSession(nextConference);
        }
      },
      (err) => {
        joinRequestRef.current = false;
        setIsLoading(false);

        if (err?.response?.data?.code === "requires_sign_in") {
          setRequiresSignIn(true);
          return;
        }

        const errorText = t("conferences.conference_has_ended");
        setErrorMessage(errorText);
        notifyError(errorText);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, linkToken]);

  const goHome = () => {
    if (anonUser.current) {
      handleLogoutUser();
    }
    navigate("/login", { replace: true });
  };

  const goToSignIn = () => {
    if (anonUser.current) {
      handleLogoutUser();
    }
    const joinSearch = token
      ? `token=${encodeURIComponent(token)}`
      : `link_token=${encodeURIComponent(linkToken)}`;
    navigate("/login", {
      replace: true,
      state: { backTo: `/conferences/join-in-person?${joinSearch}`, conferenceSignIn: true },
    });
  };

  const [nameInput, setNameInput] = useState("");

  const handleNameSubmit = (event) => {
    event.preventDefault();
    const nextConference = conference;
    if (!nextConference) {
      setErrorMessage(t("general.something_wrong"));
      return;
    }
    if (nameInput.trim().length < 3) {
      return;
    }

    setIsLoading(true);
    updateUser(
      { name: nameInput.trim(), call_language: user?.call_language || "en-US" },
      (response) => {
        const updatedUser = response?.data?.user;
        if (updatedUser) {
          handleLoginUser(updatedUser);
        }
        setShowOnboarding(false);
        startTranslationSession(nextConference);
      },
      () => {
        setIsLoading(false);
        notifyError(t("general.something_wrong"));
      }
    );
  };

  if (showOnboarding) {
    return (
      <Page>
        <Card as="form" onSubmit={handleNameSubmit}>
          <Title>{t("conferences.joining_conference")}</Title>
          <NameInput
            autoFocus
            placeholder={t("general.name")}
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
          />
          <PrimaryButton
            type="submit"
            disabled={nameInput.trim().length < 3}
            style={{ padding: "12px 16px" }}
          >
            {t("general.continue")}
          </PrimaryButton>
        </Card>
      </Page>
    );
  }

  return (
    <SpinnerComponent isLoading={isLoading}>
      <Page>
        {showInAppBrowserBanner && <InAppBrowserBanner />}
        <Card>
          {!errorMessage && !requiresSignIn && <Title>{t("general.loading")}</Title>}
          {requiresSignIn ? (
            <>
              <Message>{t("conferences.already_have_account")}</Message>
              <PrimaryButton onClick={goToSignIn} style={{ padding: "12px 16px" }}>
                {t("authentication.password.signin_button")}
              </PrimaryButton>
            </>
          ) : errorMessage ? (
            <>
              <Message>{errorMessage}</Message>
              <PrimaryButton onClick={goHome} style={{ padding: "12px 16px" }}>
                {t("general.ok")}
              </PrimaryButton>
            </>
          ) : (
            <Message>{t("conferences.joining_conference")}</Message>
          )}
        </Card>
      </Page>
    </SpinnerComponent>
  );
};

export default ConferenceInPersonJoinPage;

const Page = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100%;
  padding: 16px;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: ${({ theme }) => theme.menu.groupContentBackground};
  border-radius: 8px;
  padding: 20px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
  text-align: center;
`;

const Title = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  font-size: 20px;
  font-weight: 600;
`;

const Message = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 14px;
  line-height: 20px;
`;

const NameInput = styled.input`
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  background: ${({ theme }) => theme.menu.enterPhoneNumberBackground};
  color: ${({ theme }) => theme.menu.primaryTextColor};
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.premium_green || "#11B973"};
  }
`;
